import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FaSort, FaTrashAlt, FaAngleDoubleLeft, FaAngleLeft, FaAngleRight, FaAngleDoubleRight } from "react-icons/fa";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const HouseHunterSettlements = () => {
  const [huntersettlements, setHunterSettlements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPropertyHunter();
  }, []);

  const fetchPropertyHunter = async () => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/settlementcheck/househunter`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setHunterSettlements(response.data.response);
      } else {
        setHunterSettlements([]);
      }
    } catch (err) {
      setError("Failed to fetch house hunter settlements.");
      toast.error(err.response?.data?.response || "Failed to fetch data.");
      console.error("Error fetching Hunter Settlements:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (endpoint, fileType) => {
    const token = localStorage.getItem("token");
    setIsLoading(true);
    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { format: fileType },
        responseType: "blob",
      });
      const fileExtension = fileType === "pdf" ? "pdf" : "csv";
      const fileName = `househuntersettlementreport_${new Date().toISOString()}.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download is successful");
    } catch (err) {
      console.error(`Error downloading ${fileType.toUpperCase()}:`, err);
      toast.error(err.response?.data?.response || `Failed to download ${fileType.toUpperCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (settlementId) => {
    if (window.confirm("Are you sure you want to delete this settlement?")) {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      try {
        const response = await axios.delete(
          `${StandardURL}/iNyumba/admin/settlementcheck/deletepropertyhuntersettlement/${settlementId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Settlement deleted successfully");
        setHunterSettlements(prevSettlements => prevSettlements.filter(s => s.settlement_id !== settlementId));
      } catch (err) {
        console.error("Error deleting settlement:", err);
        toast.error(err.response?.data?.response || "Failed to delete the settlement");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedSettlements = [...huntersettlements].sort((a, b) => {
    if (sortConfig.key !== null) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedSettlements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedSettlements.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Pagination UI rendering logic
  const pageNumbers = [];
  const maxPageNumbersToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1);
  if (endPage - startPage + 1 < maxPageNumbersToShow) {
    startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Property Hunter Settlements</h1>
          
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/admin/settlementreport/propertyhuntersettlementme", "csv")}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md text-sm flex items-center"
            >
              <FontAwesomeIcon icon={faFileCsv} className="mr-1" /> Download All CSV
            </button>
            <button
              onClick={() => handleDownload("https://backend.inyumba.co.ke/iNyumba/admin/settlementreport/propertyhuntersettlementme", "pdf")}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md text-sm flex items-center"
            >
              <FontAwesomeIcon icon={faFilePdf} className="mr-1" /> Download All PDF
            </button>
          </div>

          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-red-500 font-semibold">
                {error}
              </div>
            ) : huntersettlements.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No house hunter settlements available.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {["settlement_id", "user_id", "total_amount_paid", "reference", "paymentreference", "status", "created_at"].map((header) => (
                          <th
                            key={header}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => requestSort(header)}
                          >
                            <div className="flex items-center">
                              {header.replace(/_/g, ' ').toUpperCase()}
                              <FaSort className="ml-1 text-gray-400" />
                              {getSortIcon(header)}
                            </div>
                          </th>
                        ))}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map((settlement) => (
                        <tr key={settlement.settlement_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{settlement.settlement_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{settlement.user_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">Ksh {settlement.total_amount_paid}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{settlement.reference}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{settlement.paymentreference}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{settlement.status}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(settlement.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleDelete(settlement.settlement_id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Settlement"
                            >
                              <FaTrashAlt className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <nav className="flex items-center justify-center space-x-2 mt-6 p-4">
                    <button
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleLeft />
                    </button>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleLeft />
                    </button>
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border border-gray-300 transition-colors duration-150 ease-in-out
                          ${currentPage === number 
                            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' 
                            : 'text-gray-700 bg-white hover:bg-gray-100'}`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleRight />
                    </button>
                    <button
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaAngleDoubleRight />
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default HouseHunterSettlements;